import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiUserCheck, FiDollarSign, FiShield, FiClock, FiTrendingUp, FiFileText, FiUsers, FiBriefcase, FiHeart } = FiIcons;

const HomePage = () => {
  const howItWorksSteps = [
    {
      icon: FiSearch,
      title: "Tell us what you need",
      description: "Describe your legal issue and we'll match you with the right lawyer"
    },
    {
      icon: FiUserCheck,
      title: "Get matched instantly",
      description: "Our AI connects you with verified lawyers offering discounts"
    },
    {
      icon: FiDollarSign,
      title: "Save on legal fees",
      description: "Get professional legal help at discounted rates"
    }
  ];

  const legalServices = [
    {
      icon: FiFileText,
      title: "Legal Consultation",
      description: "Get expert advice on any legal matter",
      discount: "Up to 40% off"
    },
    {
      icon: FiFileText,
      title: "Contract Review",
      description: "Professional contract drafting and review",
      discount: "Up to 35% off"
    },
    {
      icon: FiBriefcase,
      title: "Business Formation",
      description: "LLC, Corporation, and Partnership setup",
      discount: "Up to 50% off"
    },
    {
      icon: FiHeart,
      title: "Family Law",
      description: "Divorce, custody, and family legal matters",
      discount: "Up to 30% off"
    }
  ];

  const whyChooseUs = [
    {
      icon: FiShield,
      title: "Verified Lawyers",
      description: "All lawyers are licensed and background-checked"
    },
    {
      icon: FiDollarSign,
      title: "Guaranteed Savings",
      description: "Save 30-50% on standard legal fees"
    },
    {
      icon: FiClock,
      title: "Quick Response",
      description: "Get matched with lawyers within 24 hours"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Save on Legal Services –<br />
              <span className="text-green-300">Trusted Lawyers</span> with Exclusive Discounts
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto"
            >
              Get legal help from verified lawyers at lower prices. No ads. No hassle.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link
                to="/request-service"
                className="bg-white text-blue-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-all transform hover:scale-105 shadow-lg"
              >
                Get a Legal Discount
              </Link>
              <Link
                to="/for-lawyers"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-all transform hover:scale-105"
              >
                List Your Law Firm
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-12 flex justify-center items-center space-x-8 text-blue-100"
            >
              <div className="text-center">
                <div className="text-2xl font-bold">500+</div>
                <div className="text-sm">Verified Lawyers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">10k+</div>
                <div className="text-sm">Happy Clients</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">40%</div>
                <div className="text-sm">Average Savings</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get connected with the right lawyer in three simple steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorksSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="bg-blue-700 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={step.icon} className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Legal Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Popular Legal Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Find discounted legal services for your specific needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {legalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg card-hover border border-gray-100"
              >
                <div className="bg-green-100 text-green-700 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-3">
                  {service.description}
                </p>
                <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium inline-block">
                  {service.discount}
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/request-service"
              className="btn-primary text-white px-8 py-4 rounded-lg font-semibold text-lg inline-block"
            >
              Browse All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose LegalCoupon */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose LegalCoupon?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Trust, savings, and speed - everything you need for legal services
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyChooseUs.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="bg-blue-700 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={benefit.icon} className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Elements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="trust-badge p-6 rounded-lg">
              <SafeIcon icon={FiShield} className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">100% Secure</h3>
              <p className="text-gray-600">Your information is protected with bank-level security</p>
            </div>
            <div className="trust-badge p-6 rounded-lg">
              <SafeIcon icon={FiUserCheck} className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Verified Lawyers</h3>
              <p className="text-gray-600">All lawyers are licensed and background-checked</p>
            </div>
            <div className="trust-badge p-6 rounded-lg">
              <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Proven Results</h3>
              <p className="text-gray-600">Thousands of successful legal connections</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Save on Legal Services?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Join thousands of clients who have saved money on quality legal help
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/request-service"
              className="bg-white text-blue-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-all transform hover:scale-105"
            >
              Get Started Now
            </Link>
            <Link
              to="/for-lawyers"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-all"
            >
              Join as a Lawyer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;